import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import { useAuth } from '../services/auth.service';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const settings = ['Profile', 'Delete Cache', 'Logout'];

// Styled components
const ModernAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#ffffff',
  color: '#111b21',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid #e9edef',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginRight: '24px',
}));

const Logo = styled('img')(({ theme }) => ({
  width: 40,
  height: 40,
  objectFit: 'contain',
}));

const AppTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#111b21',
  marginLeft: '8px',
}));

const ModernButton = styled(Button)(({ theme }) => ({
  color: '#008069',
  backgroundColor: 'transparent',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  padding: '8px 16px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#e8f5e8',
    color: '#006d5a',
  },
  '&.active': {
    backgroundColor: '#008069',
    color: '#ffffff',
  },
}));

const MenuButton = styled(Button)(({ theme }) => ({
  color: '#008069',
  backgroundColor: 'transparent',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  padding: '8px 16px',
  borderRadius: '8px',
  border: '1px solid #e9edef',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#008069',
  },
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  color: '#008069',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: '#e8f5e8',
  },
  marginRight: '8px',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: '#008069',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
    transform: 'scale(1.05)',
    transition: 'all 0.2s ease',
  },
}));

const ModernMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#ffffff',
    border: '1px solid #e9edef',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    marginTop: '8px',
  },
}));

const ModernMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '14px',
  color: '#111b21',
  padding: '12px 16px',
  '&:hover': {
    backgroundColor: '#f0f2f5',
  },
}));

const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

function ResponsiveAppBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [anchorElViews, setAnchorElViews] = React.useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = React.useState(null);
  const [selectedReminder, setSelectedReminder] = React.useState(null);
  const [showReminderDetail, setShowReminderDetail] = React.useState(false);
  const { user, logout, currentConfig, changeCurrentConfig, getCurrentConfig } = useAuth();
  const [pages, setPages] = React.useState([]);
  const [reminders, setReminders] = React.useState([]);
  const [recentReminders, setRecentReminders] = React.useState([]);

  const navigate = useNavigate();

  // Load reminders from API
  const loadReminders = React.useCallback(async () => {
    if (!user || !user.accessToken) return;
    
    try {
      let url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/reminders`;
      const params = new URLSearchParams();
      
      if (user && (user.ejecutivo?._id)) {
        params.append('userId', user.ejecutivo?._id);
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const config = {
        headers: {
          "x-access-token": user.accessToken,
          "Content-Type": "application/json",
        },
      };
      
      const response = await axios.get(url, config);
      if (response.data) {
        const remindersData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        setReminders(remindersData);
        
        // Get recent reminders (last 5, sorted by reminder date, excluding expired ones)
        // Prioritize unread reminders
        const now = new Date();
        const nonExpiredReminders = remindersData.filter(reminder => {
          const reminderDateValue = reminder.note?.reminder || reminder.reminder || reminder.reminderDate;
          if (!reminderDateValue) return false;
          const reminderDate = new Date(reminderDateValue);
          return reminderDate >= now;
        });
        
        // Sort: unread first, then by date (newest first)
        const sortedReminders = [...nonExpiredReminders].sort((a, b) => {
          // Check if reminder is read (document level readed takes precedence)
          const aIsRead = a.readed === true;
          const bIsRead = b.readed === true;
          
          // Unread reminders come first
          if (aIsRead !== bIsRead) {
            return aIsRead ? 1 : -1;
          }
          
          // Then sort by date (newest first)
          const dateA = new Date(a.note?.reminder || a.reminder || a.reminderDate || 0);
          const dateB = new Date(b.note?.reminder || b.reminder || b.reminderDate || 0);
          return dateB - dateA;
        });
        setRecentReminders(sortedReminders.slice(0, 5));
      }
    } catch (error) {
      console.error("Error loading reminders:", error);
    }
  }, [user]);

  React.useEffect(() => {
    try {
        if (user) {
        if (user.roles.includes("ROLE_ADMIN")) {
          setPages(['chat', 'department', 'renovacion', 'ejecutivo', 'template', 'tag', 'customtable/cobros', 'customtable/cotizacion', 'customtable/cotizacionincendio', 'subscriptions', 'triggers', 'survey', 'renovacion_automatizacion', 'automatizaciones', 'customexcel', 'asegurados', 'message-queries', 'flujos', 'ventas-cruzadas']);
        } else if (user.roles.includes("ROLE_USER")) {
          setPages([...new Set(user.views.map((view) => view.value))]);
        }
      }
    } catch (error) {
      //delete the user from localstorage
      localStorage.removeItem('user');
      //reload the page
      window.location.reload();
    }

  }, [user]);

  // Load reminders on mount and when user changes
  React.useEffect(() => {
    if (user && user.accessToken) {
      loadReminders();
      // Refresh reminders every 2 minutes
      const interval = setInterval(loadReminders, 120000);
      return () => clearInterval(interval);
    }
  }, [user, loadReminders]);

  const handleOpenNavMenu = (event) => {
    //check if the page end in /chat
    if (window.location.pathname.endsWith("/chat")) {
      //back to contacts list
      changeCurrentConfig({ currentPage: "contacts", currentContact: null });
      //navigate to chat
      navigate('/dashboard/chat');
    } else {
      setAnchorElNav(event.currentTarget);
    }
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenViewsMenu = (event) => {
    setAnchorElViews(event.currentTarget);
  };

  const handleOpenNotificationsMenu = (event) => {
    setAnchorElNotifications(event.currentTarget);
    loadReminders();
  };

  const handleCloseNotificationsMenu = () => {
    setAnchorElNotifications(null);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Sin fecha';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mark reminder as read
  const markReminderAsRead = React.useCallback(async (reminder) => {
    if (!user || !user.accessToken) return;
    
    try {
      const recordId = reminder.recordId;
      const reminderDateValue = reminder.note?.reminder || reminder.reminder || reminder.reminderDate;
      
      if (!recordId || !reminderDateValue) return;
      
      const url = `${process.env.REACT_APP_BACKEND_API}/ventas-cruzadas/reminders/${recordId}/mark-read`;
      const config = {
        headers: {
          "x-access-token": user.accessToken,
          "Content-Type": "application/json",
        },
      };
      
      const body = {
        reminderTimestamp: reminderDateValue
      };
      
      await axios.put(url, body, config);
      
      // Reload reminders to get updated read status
      loadReminders();
    } catch (error) {
      console.error("Error marking reminder as read:", error);
    }
  }, [user, loadReminders]);

  const handleReminderClick = (reminder) => {
    setSelectedReminder(reminder);
    setShowReminderDetail(true);
    handleCloseNotificationsMenu();
    
    // Mark reminder as read when clicked
    markReminderAsRead(reminder);
  };

  const handleAddToGoogleCalendar = (reminder) => {
    const reminderDateValue = reminder.note?.reminder || reminder.reminder || reminder.reminderDate;
    if (!reminderDateValue) return;

    const reminderDate = new Date(reminderDateValue);
    const endDate = new Date(reminderDate.getTime() + 60 * 60 * 1000); // Add 1 hour for end time

    // Format dates as YYYYMMDDTHHmmssZ (UTC)
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const startDateStr = formatGoogleDate(reminderDate);
    const endDateStr = formatGoogleDate(endDate);

    // Get timezone (default to America/Caracas, but can be changed)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Caracas';
    
    // Build the event details
    const contactName = reminder.nombreCompleto || reminder.contactName || 'Contacto';
    const eventTitle = encodeURIComponent(`VENTAS CRUZADAS - ${contactName}`);
    const eventDetails = encodeURIComponent(
      `Contacto: ${reminder.nombreCompleto || reminder.contactName || 'Sin nombre'}\n` +
      (reminder.telefono ? `Teléfono: ${reminder.telefono}\n` : '') +
      `Nota: ${reminder.note?.text || reminder.text || 'Sin descripción'}`
    );
    const location = encodeURIComponent('');

    // Build Google Calendar URL
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${eventTitle}&dates=${startDateStr}/${endDateStr}&details=${eventDetails}&location=${location}&ctz=${timezone}`;

    // Open in new window
    window.open(googleCalendarUrl, '_blank');
  };

  const handleCloseNavMenu = (page) => {
    //check if page is string
    if (typeof page === 'string') {
      const path = user.roles.includes("ROLE_ADMIN") ? '/admin/' : '/dashboard/';
      navigate(path + page)
      setAnchorElNav(null);
    }
    else {
      setAnchorElViews(null);
    }
  };

  const handleCloseUserMenu = (setting) => {
    if (setting === 'Logout') {
      logout();
    }
    else if (setting === 'Profile') {
      navigate('/dashboard/profile')
    }
    else if (setting === 'Delete Cache') {
      localStorage.clear();
      window.location.reload();
    }
    setAnchorElUser(null);
  };

  // Get current page to highlight active button
  const getCurrentPage = () => {
    const path = window.location.pathname;
    const page = path.split('/').pop();
    return page || 'chat';
  };

  // Get display name for menu items
  const getDisplayName = (page) => {
    const displayNames = {
      'message-queries': 'Consultas de Mensajes',
      'customtable/cobros': 'Cobros',
      'customtable/cotizacion': 'Cotización',
      'customtable/cotizacionincendio': 'Cotización Incendio',
      'renovacion_automatizacion': 'Renovación Automática',
      'customexcel': 'Excel Personalizado',
      'ventas-cruzadas': 'Ventas Cruzadas'
    };
    return displayNames[page] || (page.charAt(0).toUpperCase() + page.slice(1));
  };

  return (
    <ModernAppBar position="static">
      <Toolbar disableGutters sx={{ padding: '8px 16px', justifyContent: 'space-between' }}>
          {/* Left Side - Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            <BackButton
              size="medium"
              aria-label="back to dashboard"
              onClick={handleOpenNavMenu}
            >
              <ArrowBackIcon />
            </BackButton>
            
            <ModernMenu
              id="mobile-menu"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
            >
              {pages.map((page) => (
                <ModernMenuItem key={page} onClick={() => handleCloseNavMenu(page)}>
                  <Typography textAlign="center" sx={{ fontSize: '14px' }}>
                    {getDisplayName(page)}
                  </Typography>
                </ModernMenuItem>
              ))}
            </ModernMenu>
          </Box>

          {/* Left Side - Desktop */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logo and Title */}
            <LogoContainer>
              <Logo 
                src={process.env.REACT_APP_APP_LOGO || "https://firebasestorage.googleapis.com/v0/b/segenesis-cotizador.appspot.com/o/notificaciones%2FGenesisLOGO%20vida%20y%20salud%20(1).png?alt=media&token=2e7ce64b-a316-4579-b3de-37468e2fa470"} 
                alt="Logo" 
              />
                          <AppTitle>
              {process.env.REACT_APP_PHONE_NAME || 'Genesis Business'}
            </AppTitle>
            </LogoContainer>

            {/* Chat Button */}
            <ModernButton
              onClick={() => handleCloseNavMenu("chat")}
              className={getCurrentPage() === 'chat' ? 'active' : ''}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {'Chat'}
            </ModernButton>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <NavigationContainer>
                <MenuButton
                  open={Boolean(anchorElViews)}
                  aria-haspopup="true"
                  aria-expanded={Boolean(anchorElViews) ? 'true' : undefined}
                  onClick={handleOpenViewsMenu}
                  endIcon={<MenuIcon />}
                >
                  Menú
                </MenuButton>
                <ModernMenu
                  id="desktop-menu"
                  anchorEl={anchorElViews}
                  open={Boolean(anchorElViews)}
                  onClose={handleCloseNavMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  {pages.map((page) => (
                    <ModernMenuItem
                      key={page}
                      onClick={() => handleCloseNavMenu(page)}
                    >
                      <Typography sx={{ fontSize: '14px' }}>
                        {getDisplayName(page)}
                      </Typography>
                    </ModernMenuItem>
                  ))}
                </ModernMenu>
              </NavigationContainer>
            </Box>
          </Box>

          {/* Right Side - Notifications and User Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Notifications Bell */}
            {user && (user.roles?.includes("ROLE_ADMIN") || user.views?.some(v => v.value === 'ventas-cruzadas')) && (
              <Tooltip title="Recordatorios" arrow>
                <IconButton
                  onClick={handleOpenNotificationsMenu}
                  sx={{
                    color: '#111b21',
                    '&:hover': {
                      backgroundColor: '#f0f2f5',
                    },
                  }}
                >
                  <Badge 
                    badgeContent={recentReminders.filter(reminder => {
                      // Count only unread reminders (check document level readed)
                      return reminder.readed !== true;
                    }).length} 
                    color="error"
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            <ModernMenu
              sx={{ mt: '45px' }}
              id="notifications-menu"
              anchorEl={anchorElNotifications}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElNotifications)}
              onClose={handleCloseNotificationsMenu}
              PaperProps={{
                sx: {
                  width: 380,
                  maxHeight: 500,
                  overflow: 'auto',
                },
              }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid #e9edef' }}>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#111b21' }}>
                  Recordatorios
                </Typography>
                <Typography variant="caption" sx={{ color: '#667781', fontSize: '12px' }}>
                  {recentReminders.length} recordatorio{recentReminders.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              {recentReminders.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#667781' }}>
                    No hay recordatorios
                  </Typography>
                </Box>
              ) : (
                recentReminders.map((reminder, index) => {
                  const reminderDateValue = reminder.note?.reminder || reminder.reminder || reminder.reminderDate;
                  const reminderDate = reminderDateValue ? new Date(reminderDateValue) : new Date();
                  const isPast = reminderDate < new Date();
                  const isUpcoming = reminderDate > new Date() && reminderDate <= new Date(Date.now() + 24 * 60 * 60 * 1000);
                  const reminderText = String(reminder.note?.text || reminder.text || 'Sin descripción');
                  const contactName = String(reminder.nombreCompleto || reminder.contactName || 'Sin nombre');
                  const phoneNumber = reminder.telefono ? String(reminder.telefono) : null;
                  // Check if reminder is read (document level readed)
                  const isRead = reminder.readed === true;

                  return (
                    <MenuItem
                      key={index}
                      onClick={() => handleReminderClick(reminder)}
                      sx={{
                        borderBottom: index < recentReminders.length - 1 ? '1px solid #f0f2f5' : 'none',
                        backgroundColor: isRead ? 'transparent' : '#f0f8ff',
                        '&:hover': {
                          backgroundColor: isRead ? '#f8f9fa' : '#e6f3ff',
                        },
                      }}
                    >
                      <Box sx={{ width: '100%', py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 1, gap: 1 }}>
                            {!isRead && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: '#008069',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '13px',
                                fontWeight: isRead ? 500 : 600,
                                color: isRead ? '#667781' : '#111b21',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}
                            >
                              {contactName}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {isPast && (
                              <Box
                                sx={{
                                  bgcolor: '#fee',
                                  color: '#c00',
                                  fontSize: '10px',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: '10px',
                                  fontWeight: 600,
                                }}
                              >
                                Vencido
                              </Box>
                            )}
                            {isUpcoming && !isPast && (
                              <Box
                                sx={{
                                  bgcolor: '#fff4e6',
                                  color: '#d97706',
                                  fontSize: '10px',
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: '10px',
                                  fontWeight: 600,
                                }}
                              >
                                Próximo
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {phoneNumber && (
                          <Typography variant="caption" sx={{ color: '#667781', fontSize: '11px', display: 'block', mb: 0.5 }}>
                            {phoneNumber}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#667781',
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                          }}
                        >
                          {reminderText}
                        </Typography>
                        <Typography variant="caption" sx={{ color: isPast ? '#c00' : '#d97706', fontSize: '11px', fontWeight: 500 }}>
                          {formatTimestamp(reminderDateValue)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })
              )}
            </ModernMenu>
            <Tooltip title="Configuración" arrow>
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <UserAvatar>
                  {user?.username ? user.username.charAt(0).toUpperCase() : "U"}
                </UserAvatar>
              </IconButton>
            </Tooltip>
            <ModernMenu
              sx={{ mt: '45px' }}
              id="user-menu"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <ModernMenuItem key={setting} onClick={() => handleCloseUserMenu(setting)}>
                  <Typography textAlign="center" sx={{ fontSize: '14px' }}>
                    {setting}
                  </Typography>
                </ModernMenuItem>
              ))}
            </ModernMenu>
          </Box>
        </Toolbar>
        
        {/* Reminder Detail Modal */}
        {showReminderDetail && selectedReminder && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
            onClick={() => setShowReminderDetail(false)}
          >
            <Box
              sx={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                width: '90%',
                maxWidth: 600,
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <Box sx={{ p: 3, borderBottom: '1px solid #e9edef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ fontSize: '20px', fontWeight: 600, color: '#111b21' }}>
                  Detalle del Recordatorio
                </Typography>
                <IconButton
                  onClick={() => setShowReminderDetail(false)}
                  sx={{
                    color: '#667781',
                    '&:hover': {
                      backgroundColor: '#f0f2f5',
                    },
                  }}
                >
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </IconButton>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                {(() => {
                  const reminderDateValue = selectedReminder.note?.reminder || selectedReminder.reminder || selectedReminder.reminderDate;
                  const reminderDate = reminderDateValue ? new Date(reminderDateValue) : new Date();
                  const isPast = reminderDate < new Date();
                  const isUpcoming = reminderDate > new Date() && reminderDate <= new Date(Date.now() + 24 * 60 * 60 * 1000);
                  const reminderText = String(selectedReminder.note?.text || selectedReminder.text || 'Sin descripción');
                  const contactName = String(selectedReminder.nombreCompleto || selectedReminder.contactName || 'Sin nombre');
                  const phoneNumber = selectedReminder.telefono ? String(selectedReminder.telefono) : null;

                  return (
                    <>
                      {/* Status Badge */}
                      <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
                        {isPast && (
                          <Box
                            sx={{
                              bgcolor: '#fee',
                              color: '#c00',
                              fontSize: '12px',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '12px',
                              fontWeight: 600,
                            }}
                          >
                            Vencido
                          </Box>
                        )}
                        {isUpcoming && !isPast && (
                          <Box
                            sx={{
                              bgcolor: '#fff4e6',
                              color: '#d97706',
                              fontSize: '12px',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '12px',
                              fontWeight: 600,
                            }}
                          >
                            Próximo
                          </Box>
                        )}
                      </Box>

                      {/* Contact Information */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" sx={{ color: '#667781', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Contacto
                        </Typography>
                        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, color: '#111b21', mt: 0.5 }}>
                          {contactName}
                        </Typography>
                        {phoneNumber && (
                          <Typography variant="body2" sx={{ color: '#667781', fontSize: '14px', mt: 0.5 }}>
                            📞 {phoneNumber}
                          </Typography>
                        )}
                      </Box>

                      {/* Reminder Date */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" sx={{ color: '#667781', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Fecha y Hora
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: 500, color: isPast ? '#c00' : '#d97706', mt: 0.5 }}>
                          📅 {formatTimestamp(reminderDateValue)}
                        </Typography>
                      </Box>

                      {/* Reminder Text */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" sx={{ color: '#667781', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Nota
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: '14px', 
                            color: '#111b21', 
                            mt: 0.5, 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                          }}
                        >
                          {reminderText}
                        </Typography>
                      </Box>

                      {/* Additional Info */}
                      {selectedReminder.note?.user && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="caption" sx={{ color: '#667781', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Creado por
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '14px', color: '#111b21', mt: 0.5 }}>
                            {selectedReminder.note.user}
                          </Typography>
                          {selectedReminder.note.timestamp && (
                            <Typography variant="caption" sx={{ fontSize: '12px', color: '#667781', mt: 0.5, display: 'block' }}>
                              {formatTimestamp(selectedReminder.note.timestamp)}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Actions */}
                      <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: '1px solid #e9edef' }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setShowReminderDetail(false);
                            navigate(user?.roles?.includes("ROLE_ADMIN") ? '/admin/ventas-cruzadas' : '/dashboard/ventas-cruzadas');
                          }}
                          sx={{
                            flex: 1,
                            textTransform: 'none',
                            borderColor: '#008069',
                            color: '#008069',
                            '&:hover': {
                              borderColor: '#006d5a',
                              backgroundColor: '#e8f5e8',
                            },
                          }}
                        >
                          Ver en Ventas Cruzadas
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleAddToGoogleCalendar(selectedReminder)}
                          sx={{
                            flex: 1,
                            textTransform: 'none',
                            backgroundColor: '#008069',
                            '&:hover': {
                              backgroundColor: '#006d5a',
                            },
                          }}
                          startIcon={
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          }
                        >
                          Agregar a Google Calendar
                        </Button>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Box>
          </Box>
        )}
      </ModernAppBar>
  );
}

export default ResponsiveAppBar;